import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { Checkbox, Chip, TextField, Typography } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { MdCheckBoxOutlineBlank as CheckBoxOutlineBlankIcon } from 'react-icons/md';
import { MdCheckBox as CheckBoxIcon } from 'react-icons/md';
import { useAuth0 } from '@auth0/auth0-react';
import { getActiveMembersSuggestions } from '../../application-owners/api/applications-owners-api';
import { AppOwnerType } from 'common-types';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const MuitipleAppOwnersInput: FC<
  React.PropsWithChildren<MuitipleAppOwnersInputProps>
> = ({ onChange, options, preSelectedOptions, label }) => {
  const { user } = useAuth0();
  const [isLoadingSuggestions, setIsloadingSuggestions] = useState(false);
  const [preSelectedStateOptions, setPreSelectedOptions] =
    useState(preSelectedOptions);

  const handleOnChange = useCallback(
    (_event, value, reason) => {
      if (['createOption', 'selectOption', 'removeOption'].includes(reason)) {
        const updateChips = [...value];
        onChange && onChange(updateChips);
        preSelectedStateOptions && setPreSelectedOptions(updateChips);
      }
      if (reason === 'clear') {
        onChange && onChange([]);
        preSelectedStateOptions && setPreSelectedOptions([]);
      }
    },
    [onChange, preSelectedStateOptions],
  );

  useEffect(() => {
    setIsloadingSuggestions(true);
    (async () => {
      await getActiveMembersSuggestions();
      setIsloadingSuggestions(false);
    })();
  }, []);

  return (
    <Autocomplete
      loading={isLoadingSuggestions}
      isOptionEqualToValue={(option, value) => {
        return option.email === value.email;
      }}
      value={preSelectedStateOptions}
      forcePopupIcon={false}
      limitTags={2}
      disableCloseOnSelect
      onChange={handleOnChange}
      size='small'
      renderTags={(value: readonly AppOwnerType[], getTagProps) => {
        return value.map((option: AppOwnerType, index: number) => {
          return (
            <Chip
              size='small'
              color='primary'
              variant='filled'
              label={option.email}
              {...getTagProps({ index })}
            />
          );
        });
      }}
      disablePortal
      filterOptions={createFilterOptions({
        matchFrom: 'any',
        stringify: option => option.email + option.name,
      })}
      fullWidth
      options={options}
      getOptionLabel={option => option.email}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            size='small'
            icon={icon}
            checkedIcon={checkedIcon}
            checked={selected}
          />
          <Typography variant='caption'>
            {user?.email === option.email
              ? `Me (${option.email})`
              : option.name}
          </Typography>
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          variant='standard'
          label={label}
          size='small'
          placeholder='username@domain.com'
        />
      )}
      multiple
    />
  );
};

export interface MuitipleAppOwnersInputProps {
  onChange?: (selectedOwners: AppOwnerType[]) => void;
  options: AppOwnerType[];
  preSelectedOptions?: AppOwnerType[];
  label?: ReactNode;
}

export default MuitipleAppOwnersInput;
